import React from 'react';
import { Helmet } from 'react-helmet';
import Head from 'next/head'

export enum UrlType {
  ABSOLUTE = 'ABSOLUTE',
  RELATIVE = 'RELATIVE',
}

export type Url = {
  type: UrlType;
  href: string;
}

export enum MetaTagType {
  Title = 'title',
  MetaName = 'metaName',
  MetaProperty = 'metaProperty',
  Link = 'link',
  HttpEquiv = 'httpEquiv',
  HrefLang = 'hrefLang',
}

export type MetaTag = {
  type: MetaTagType.Title;
  title: string;
} | {
  type: MetaTagType.MetaProperty;
  property: string;
  content: string;
} | {
  type: MetaTagType.MetaName;
  name: string;
  content: string;
} | {
  type: MetaTagType.Link;
  rel: string;
  href: string;
} | {
  type: MetaTagType.HttpEquiv;
  httpEquiv: 'content-language';
  content: string;
} | {
  type: MetaTagType.HrefLang;
  hrefLang: string;
  href: string;
};

export type MetaTagWithKey = MetaTag & {
  key: string;
};

export type HreflangMeta = {
  langCode: string;
  path: Url;
};

export type TwitterMeta = {
  card: string;
  title: string;
  site?: string | null | undefined;
  description?: string | null | undefined;
  image?: Url | null | undefined;
};

export type OpenGraphMeta = {
  url: Url;
  title: string;
  description: string;
  image?: Url | undefined | null;
  imageSecure?: Url | undefined | null;
  contentType?: string | undefined | null;
  locale?: string | undefined | null;
  siteName?: string | undefined | null;
  articlePublisher?: string | undefined | null;
  articleSection?: string | undefined | null;
};

export type MetaData = {
  title: string;
  description?: string | undefined | null;
  keywords: string[] | null | undefined;
  canonical?: Url;
  noindex?: boolean | null | undefined;
  nofollow?: boolean | null | undefined;
  hreflangTags: HreflangMeta[];
  twitter?: TwitterMeta | null | undefined;
  og?: OpenGraphMeta | null | undefined;
};

const buildNameTags = (
  record: Record<string, string | undefined | null>
) => {
  let tags: MetaTagWithKey[] = [];
  for (const key in record) {
    const content = record[key];
    if (content) {
      tags = [...tags, {
        key: `name:${key}`,
        type: MetaTagType.MetaName,
        name: key,
        content,
      }]
    }
  }

  return tags;
};

const buildPropertyTags = (
  record: Record<string, string | undefined | null>,
) => {
  let tags: MetaTagWithKey[] = [];
  for (const key in record) {
    const content = record[key];
    if (content) {
      tags = [...tags, {
        key: `property:${key}`,
        type: MetaTagType.MetaProperty,
        property: key,
        content,
      }]
    }
  }

  return tags;
};

export const addBasePathToUrl = (basePath: string, href: string | undefined) => href
  ? `${basePath}${href}`
  : basePath;

export const buildUrl = (basePath: string, url: Url | undefined, addTrailingSlash = false) => {
  const result = url?.type === UrlType.ABSOLUTE ? url?.href : addBasePathToUrl(basePath, url?.href);
  if (addTrailingSlash && !result.endsWith('/')) {
    return `${result}/`;
  }
  return result;
};

export const hooks = {
  useMetadataTags(
    basePath: string,
    metadata: MetaData | undefined,
    language: string,
    defaultLanguage: string,
    withoutXdefaultTag = false,
  ) {
    return React.useMemo(() => {
      if (!metadata) {
        return [];
      }

      const robotsContent =
        `${metadata.noindex ? 'noindex' : 'index'}, ${metadata.nofollow ? 'nofollow' : 'follow'}`;
      let result: MetaTagWithKey[] = [{
        key: 'title',
        type: MetaTagType.Title,
        title: metadata.title,
      }, {
        key: 'name:robots',
        type: MetaTagType.MetaName,
        name: 'robots',
        content: robotsContent,
      }] as MetaTagWithKey[];

      if (metadata.description) {
        result = [...result, {
          key: 'name:description',
          type: MetaTagType.MetaName,
          name: 'description',
          content: metadata.description,
        }]
      }

      if (metadata.keywords && metadata.keywords.length) {
        const keywordsContent = metadata.keywords.join(', ');
        result = [...result, {
          key: 'name:keywords',
          type: MetaTagType.MetaName,
          name: 'keywords',
          content: keywordsContent,
        }]
      }

      if (metadata.canonical) {
        result = [...result, {
          key: 'link:canonical',
          type: MetaTagType.Link,
          rel: 'canonical',
          href: buildUrl(basePath, metadata.canonical, true),
        }];
      }

      if (metadata.twitter) {
        result = [...result, ...buildNameTags({
          'twitter:card': metadata.twitter.card,
          'twitter:description': metadata.twitter.description || '',
          'twitter:image': metadata.twitter.image && buildUrl(basePath, metadata.twitter.image),
          'twitter:site': metadata.twitter.site || '',
          'twitter:title': metadata.twitter.title,
        })];
      }

      if (metadata.og) {
        result = [...result, ...buildPropertyTags({
          'og:title': metadata.og.title,
          'og:description': metadata.og.description,
          'og:image': metadata.og.image ? buildUrl(basePath, metadata.og.image) : null,
          'og:url': buildUrl(basePath, metadata.og.url, true),
          'og:image:secure_url': metadata.og.imageSecure ?
            buildUrl(basePath, metadata.og.imageSecure) : null,
          'og:type': metadata.og.contentType,
          'locale': metadata.og.locale,
          'siteName': metadata.og.siteName,
          'articlePublisher': metadata.og.articlePublisher,
          'articleSection': metadata.og.articleSection,
        })];
      }

      if (metadata.hreflangTags?.length && language.length) {
        let defaultHref: string | null = null;
        if (withoutXdefaultTag) {
          defaultHref = null;
        } else {
          defaultHref = buildUrl(
            basePath,
            metadata.hreflangTags.find(item => item.langCode === defaultLanguage)?.path,
            true,
          );
        }

        result = [...result, {
          key: 'content-language',
          type: MetaTagType.HttpEquiv,
          httpEquiv: 'content-language',
          content: language,
        }, defaultHref ? {
          key: 'hreflang:x-default',
          type: MetaTagType.HrefLang,
          hrefLang: 'x-default',
          href: defaultHref,
        } : {} as MetaTagWithKey];
      }

      if (metadata.hreflangTags?.length) {
        result = [...result, ...metadata.hreflangTags.map(item => ({
          key: `hreflang:${item.langCode}`,
          type: MetaTagType.HrefLang,
          hrefLang: item.langCode,
          href: withoutXdefaultTag ? item.path.href : buildUrl(basePath, item.path, true),
        })) as MetaTagWithKey[]];
      }

      return result;
    }, [metadata, language, basePath, withoutXdefaultTag, defaultLanguage]);
  },
};

export type MetadataBaseProps = {
  metaData?: MetaData;
  language: string;
  defaultLanguage: string;
  basePath: string;
  withoutXdefaultTag?: boolean;
}
export enum MetaWrapperType {
  Next = 'next',
  Helmet = 'helmet',
}
export type Props = MetadataBaseProps & {
  wrapperType: MetaWrapperType;
};

export const MetadataHelmet = React.memo((props: Props) => {
  const {
    wrapperType,
    basePath,
    metaData,
    language,
    defaultLanguage,
    withoutXdefaultTag,
  } = props;

  const metaTags = hooks.useMetadataTags(
    basePath,
    metaData,
    language,
    defaultLanguage,
    withoutXdefaultTag
  );

  if (!metaTags.length) {
    return null;
  }

  const tags = metaTags.map(({ key, ...tag}) => {
    switch (tag.type) {
      case MetaTagType.Title:
        return <title key={key}>{tag.title}</title>;
  
      case MetaTagType.MetaName:
        return (
          <meta
            name={tag.name}
            content={tag.content}
            key={`name:${tag.name},content:${tag.content}`}
          />
        );
  
      case MetaTagType.MetaProperty:
        return (
          <meta
            property={tag.property}
            content={tag.content}
            key={`property:${tag.property}`}
          />
        );
  
      case MetaTagType.Link:
        return (
          <link
            rel={tag.rel}
            href={tag.href}
            key={`link:${tag.rel}`}
          />
        );
  
      case MetaTagType.HttpEquiv:
        return (
          <meta
            httpEquiv={tag.httpEquiv}
            content={tag.content}
            key={`${tag.httpEquiv}:${tag.content}`}
          />
        );
  
      case MetaTagType.HrefLang:
        return (
          <link
            rel='alternate'
            hrefLang={tag.hrefLang}
            href={tag.href}
            key={`hreflang:${tag.href}:${tag.hrefLang}`}
          />
        );
    }
  });
  
  if (wrapperType === MetaWrapperType.Helmet) {
    return (
      <Helmet>
        {tags}
      </Helmet>
    );
  }

  return (
    <Head>
      {tags}
    </Head>
  );
});

MetadataHelmet.displayName = 'MetadataHelmet';
